import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./VigilPaywall.module.scss"

/**
 * VigilPaywall (`/vigil`) — an original, self-contained EXPIRATION paywall for
 * Private Photo Vault Premium. Type rolled at random for this run: EXPIRATION.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * Premium keeps a living *watch-beat* over your vault: a steady pulse that means
 * someone is still on duty — backing up every new photo, guarding the locks,
 * holding a safety net, standing a decoy on watch. Your free trial is what keeps
 * that beat going, and it is faltering: the pulse is weak, slow and amber, the
 * four protections are dim / off-duty, and the memories sit un-watched. You tap
 * the pad to bring the beat back to a strong, steady rhythm; each tap brings one
 * real protection back on duty, the waveform sharpens from amber to brand blue,
 * and the veil lifts off the memories. Four taps and the watch is steady again.
 *
 * ── Directive (active standing directive this run) ─────────────────────────
 * "build paywalls centered around loss aversion, use fear as the driving factor.
 *  Include some amazing animations, highlight our feature list. Target new users."
 * Honored: the driver is the fear of the watch going quiet as the trial ends
 * (loss aversion on ONGOING protection, never on data). Amazing animation = a
 * living ECG-style pulse that scrolls, falters and steadies, with a metronome
 * guide-ring and a beat ripple across the memories. The four real Premium
 * protections are the heart of the layout. Framed for a NEW user whose 7-day
 * trial is about to end.
 *
 * ── Divergence (see claude-paywalls/_ledger.md + vigil.md) ─────────────────
 * Layout      : a VITAL-SIGN WATCH PANEL — a scrolling pulse waveform over a
 *               field of memory tiles with four "on duty" protection stations.
 *               Not hero+card / calendar / road / net / arch / split-flap board /
 *               quilt / deck / music box / postmark / harbour-nocturne.
 * Interaction : RHYTHMIC TAP-TO-KEEP-THE-BEAT (temporal, repeated). No prior
 *               paywall used a rhythmic multi-tap; distinct from hold, drag,
 *               rotary, haul, flip, stamp.
 * Register    : steadfast, vigilant warmth with the quiet dread of it going still
 *               — not cold-urgency (Deep Freeze), not nocturne (Beacon).
 * Art         : warm, light "wellness / vital monitor" UI — cream ground, brand
 *               blue = the steady on-watch state, amber = the faltering state.
 * Value frame : CONTINUITY of an ongoing watch — the service that stays on duty.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * All four protections are real Premium features (Cloud Vault backup, Break-In
 * Report photo & GPS, Trash Recovery, Decoy Passcode). Explicit copy: your photos
 * NEVER leave the vault and are never deleted when the trial ends — they stay
 * locked and safe; only the backing-up and guarding would pause. No ticking
 * countdown, no fake scarcity, real savings arithmetic. Yearly is the featured,
 * default, most-prominent price; monthly is small and secondary. Sample images
 * are labelled Unsplash demo photos, not the user's library.
 */

const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

type PlanKey = "yearly" | "monthly"

const TARGET = 4 // taps to bring the watch fully on duty

// The four real Premium protections — each comes on duty on one beat.
const STATIONS = [
  {
    key: "backup",
    Icon: svg.featureIcons.CloudVault,
    name: "Cloud Vault",
    duty: "backing up every new photo",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-In Report",
    duty: "photo & GPS if someone tries your PIN",
  },
  {
    key: "trash",
    Icon: svg.featureIcons.Trash,
    name: "Trash Recovery",
    duty: "a 30-day safety net for deletes",
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Passcode",
    duty: "a false vault stands on watch",
  },
] as const

// The visible slice of the vault the watch is keeping — everyday memories.
const TILES = [
  { src: "/vigil/morning.jpg", tag: "Sun · 8:04" },
  { src: "/vigil/family.jpg", tag: "Nana & Mia" },
  { src: "/vigil/birthday.jpg", tag: "Mia turns 5" },
  { src: "/vigil/dog.jpg", tag: "Biscuit" },
  { src: "/vigil/hike.jpg", tag: "Ridge trail" },
  { src: "/vigil/beach.jpg", tag: "Golden hour" },
]

// ── ECG shape ───────────────────────────────────────────────────────────────
// One heartbeat as a sum of gaussians over a normalised period 0..1.
const gauss = (t: number, c: number, w: number) =>
  Math.exp(-(((t - c) / w) ** 2))
const beatShape = (t: number) =>
  0.16 * gauss(t, 0.3, 0.05) - // P
  0.22 * gauss(t, 0.44, 0.018) + // Q
  1.0 * gauss(t, 0.48, 0.02) - // R
  0.34 * gauss(t, 0.52, 0.022) + // S
  0.28 * gauss(t, 0.66, 0.06) // T

const VigilPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  // Library size — prefer real debug data, fall back to a believable number.
  const watched = useMemo(() => {
    const n = state.productData?.numPhotos
    return n && n > 200 ? n : 2148
  }, [state.productData])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // beats: 0 = faltering, TARGET = steady & on watch.
  const [beats, setBeats] = useState(0)
  const [sealed, setSealed] = useState(false)
  const [bloom, setBloom] = useState(false)

  const strength = beats / TARGET // 0..1
  const litCount = beats

  // rAF drives ONLY the waveform path (via ref) — no per-frame React renders.
  const pathRef = useRef<SVGPathElement | null>(null)
  const glowRef = useRef<SVGPathElement | null>(null)
  const strengthRef = useRef(strength)
  const offsetRef = useRef(0)
  const rafRef = useRef<number | undefined>(undefined)
  const lastRef = useRef<number | null>(null)

  strengthRef.current = strength

  const W = 360
  const H = 120
  const MID = 68

  const buildPath = useCallback((offset: number, s: number) => {
    // Faltering (s→0): weak, slow, low-amplitude. Steady (s→1): crisp & regular.
    const period = lerp(150, 96, s)
    const amp = lerp(9, 42, s)
    const floorAmp = 3.2 // a faint tremor even when faltering — never a flatline
    const step = 3
    let d = ""
    for (let x = 0; x <= W; x += step) {
      const t = (((x + offset) % period) + period) % period
      const yBeat = beatShape(t / period) * amp
      // a little organic tremor so the "off-duty" line still breathes
      const tremor = Math.sin((x + offset) * 0.08) * floorAmp * (1 - s * 0.7)
      const y = MID - yBeat - tremor
      d += (x === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1) + " "
    }
    return d
  }, [])

  useEffect(() => {
    if (reduceMotion) {
      // Static, fully-steady line — no motion.
      const d = buildPath(0, 1)
      pathRef.current?.setAttribute("d", d)
      glowRef.current?.setAttribute("d", d)
      return
    }
    const tick = (ts: number) => {
      if (lastRef.current == null) lastRef.current = ts
      const dt = ts - lastRef.current
      lastRef.current = ts
      const s = strengthRef.current
      const period = lerp(150, 96, s)
      const beatSec = lerp(1.55, 0.74, s)
      offsetRef.current += (dt / 1000) * (period / beatSec)
      const d = buildPath(offsetRef.current, s)
      pathRef.current?.setAttribute("d", d)
      glowRef.current?.setAttribute("d", d)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      lastRef.current = null
    }
  }, [reduceMotion, buildPath])

  // Reduced-motion: hand the user a watch that's already steady.
  useEffect(() => {
    if (reduceMotion) {
      setBeats(TARGET)
      setSealed(true)
    }
  }, [reduceMotion])

  const beat = useCallback(() => {
    if (sealed) return
    setBeats((b) => {
      const next = Math.min(TARGET, b + 1)
      if (next >= TARGET) {
        setSealed(true)
        setBloom(true)
        window.setTimeout(() => setBloom(false), 900)
      }
      return next
    })
  }, [sealed])

  const onKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        beat()
      }
    },
    [beat],
  )

  // CSS timing/colour vars follow the current strength.
  const beatSec = lerp(1.55, 0.74, strength)
  const rootStyle = {
    ["--lit" as any]: strength.toFixed(3),
    ["--veil" as any]: (1 - strength).toFixed(3),
    ["--beat" as any]: beatSec.toFixed(2) + "s",
  }

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const statusLabel = sealed
    ? "On watch · steady"
    : beats === 0
      ? "Faltering"
      : "Coming back…"

  return (
    <div
      className={`${styles.root} ${sealed ? styles.rootOn : ""}`}
      style={rootStyle}
    >
      <div className={styles.grain} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
              <path
                d="M6.6 12.4h2.4l1.3-2.7 1.9 4.6 1.4-2.9 0.9 1.7h2.3"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <span className={styles.eyebrow}>
          <span className={styles.eyebrowDot} aria-hidden="true" />
          Free trial ending
        </span>
        <h1 className={styles.title}>
          <span
            className={`${styles.titleLine} ${sealed ? styles.titleOut : styles.titleIn}`}
          >
            Something is still keeping
            <br />
            watch over your photos.
          </span>
          <span
            className={`${styles.titleLine} ${styles.titleOverlay} ${sealed ? styles.titleIn : styles.titleOut}`}
            aria-hidden={!sealed}
          >
            The watch is steady.
            <br />
            <span className={styles.titleAccent}>
              Your vault stays covered.
            </span>
          </span>
        </h1>
        <p className={styles.sub}>
          {sealed
            ? "Premium keeps the beat going for you — every new memory backed up and guarded, automatically."
            : "When the trial ends, that watch stands down. The beat is already faltering."}
        </p>
      </div>

      {/* ── THE WATCH PANEL ───────────────────────────────────────────────── */}
      <section
        className={`${styles.panel} ${sealed ? styles.panelOn : ""}`}
        aria-label="Vault watch monitor"
      >
        {bloom && <span className={styles.bloom} aria-hidden="true" />}

        <div className={styles.panelHead}>
          <span className={styles.watching}>
            <span className={styles.watchingNum}>
              {watched.toLocaleString()}
            </span>
            memories under watch
          </span>
          <span
            className={`${styles.status} ${sealed ? styles.statusOn : beats === 0 ? styles.statusLow : ""}`}
          >
            <span className={styles.statusPip} aria-hidden="true" />
            {statusLabel}
          </span>
        </div>

        {/* memory field the watch is keeping */}
        <div className={styles.tiles} aria-hidden="true">
          {TILES.map((t, i) => (
            <figure
              key={t.src}
              className={styles.tile}
              style={{ ["--i" as any]: i }}
            >
              <img src={t.src} alt="" loading="eager" draggable={false} />
              <span className={styles.tileVeil} />
              <figcaption className={styles.tileTag}>{t.tag}</figcaption>
            </figure>
          ))}
        </div>

        {/* the living pulse */}
        <div className={styles.scope}>
          <svg
            className={styles.wave}
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={`Vault watch: ${statusLabel}. ${litCount} of ${TARGET} protections on duty.`}
          >
            <path ref={glowRef} className={styles.waveGlow} d="" />
            <path ref={pathRef} className={styles.waveLine} d="" />
          </svg>

          {/* metronome guide-ring + tap pad */}
          <button
            type="button"
            className={`${styles.pad} ${sealed ? styles.padOn : ""}`}
            onPointerDown={(e) => {
              e.preventDefault()
              beat()
            }}
            onKeyDown={onKey}
            aria-label="Tap to keep the watch beating"
          >
            <span className={styles.padRing} aria-hidden="true" />
            <span className={styles.padRing2} aria-hidden="true" />
            <span className={styles.padCore} aria-hidden="true">
              {sealed ? (
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    d="M5 12.6l4.4 4.4L19 7.2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path
                    d="M12 20.5C6.9 16.8 4 13.6 4 9.9 4 7.2 6 5.3 8.4 5.3c1.5 0 2.8.7 3.6 1.9.8-1.2 2.1-1.9 3.6-1.9C18 5.3 20 7.2 20 9.9c0 3.7-2.9 6.9-8 10.6z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </span>
          </button>
        </div>

        <p className={styles.padHint} aria-live="polite">
          {sealed ? (
            <>
              You brought it back by hand.{" "}
              <strong>Premium keeps it beating</strong> — you never have to.
            </>
          ) : beats === 0 ? (
            <>Tap the pulse to bring the watch back on duty.</>
          ) : (
            <>Keep it steady — {TARGET - beats} more to a full watch.</>
          )}
        </p>

        {/* four protection stations — come on duty one per beat */}
        <ul className={styles.stations}>
          {STATIONS.map((s, i) => {
            const on = litCount > i
            const Icon = s.Icon
            return (
              <li
                key={s.key}
                className={`${styles.station} ${on ? styles.stationOn : ""}`}
                style={{ ["--i" as any]: i }}
              >
                <span className={styles.stationIcon} aria-hidden="true">
                  <Icon />
                </span>
                <span className={styles.stationText}>
                  <span className={styles.stationName}>{s.name}</span>
                  <span className={styles.stationDuty}>{s.duty}</span>
                </span>
                <span className={styles.stationState}>
                  {on ? "On duty" : "Off"}
                </span>
              </li>
            )
          })}
        </ul>
      </section>

      {/* honest, compliant reassurance */}
      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        Your photos never leave the vault. If the watch stands down they stay
        locked and safe — only the backing-up and guarding pause.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ──────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>
              Keep the watch on · Yearly
            </span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            about {yearlyPerMonth}/mo, billed once a year
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo photos from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? hasTrial
                ? "Keep the watch on — start free"
                : "Keep the watch on"
              : "Keep the watch on monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
                : `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default VigilPaywall
